<template>
  <div class="connected-services-view full-view">
    <!-- Modal Tunnel -->
    <ModalTunnel
      v-model:visible="showModal"
      :closeOnExternalClick="false"
      :nbPages="nbTunnelPages"
      :displayNavigation="false"
      ref="refTunnel"
    >
      <!-- Provider Selection -->
      <template #[`header-${pages.PROVIDER.pageNum}`]>
        <MPHDefault :header="header" :closeButton="true" @close="closeModal" />
      </template>

      <template #[`content-${pages.PROVIDER.pageNum}`]>
        <v-col class="pa-4">
          <v-row class="ma-0 token-text-h5 mx-auto fit-content">
            Please select a provider
          </v-row>
          <v-row class="ma-0 mt-4 provider-grid">
            <v-col
              v-for="provider of providers"
              :key="provider.id"
              class="pa-0 provider-option"
              :class="{ selected: selectedProviderId === provider.id }"
              @click="selectProvider(provider)"
            >
              <Logo
                v-if="provider.logo"
                :logo="provider.logo"
                class="provider-logo"
              />
              <span v-else class="token-text-medium provider-option-label">
                {{ provider.name }}
              </span>
            </v-col>
          </v-row>
        </v-col>
      </template>

      <template #[`footer-${pages.PROVIDER.pageNum}`]>
        <MPFDefault
          :previousLabel="pages.PROVIDER.previousLabel"
          :nextLabel="pages.PROVIDER.nextLabel"
          :disableNextButton="disableNextButton"
          @previous="closeModal"
          @next="goToPage(pageKeys.API_KEY)"
        />
      </template>
      <!-- / Provider Selection -->

      <!-- API Key Input -->
      <template #[`header-${pages.API_KEY.pageNum}`]>
        <MPHDefault :header="header" :closeButton="true" @close="closeModal" />
      </template>

      <template #[`content-${pages.API_KEY.pageNum}`]>
        <v-col class="pa-4">
          <v-row class="ma-0 token-text-regular"> Connection name </v-row>
          <v-row class="ma-0 mt-2">
            <CustomTextInput
              v-model="form.name"
              placeholder="Connection name"
            />
          </v-row>
          <template v-for="field of selectedProviderRequirements" :key="field">
            <v-row class="ma-0 mt-4 token-text-regular field-label">
              {{ fieldLabel(field) }}
            </v-row>
            <v-row class="ma-0 mt-2">
              <CustomTextInput
                v-model="form[field]"
                :placeholder="fieldLabel(field)"
                type="password"
              />
            </v-row>
          </template>
        </v-col>
      </template>

      <template #[`footer-${pages.API_KEY.pageNum}`]>
        <MPFDefault
          :previousLabel="pages.API_KEY.previousLabel"
          :hidePreviousButton="editingAccountId"
          :nextLabel="pages.API_KEY.nextLabel"
          :disableNextButton="disableNextButton"
          @previous="goToPage(pageKeys.PROVIDER)"
          @next="
            goToPage(pageKeys.VALIDATION);
            saveAccount();
          "
        />
      </template>
      <!-- / API Key Input -->

      <!-- Validation -->
      <template #[`header-${pages.VALIDATION.pageNum}`]>
        <MPHDefault :header="header" :closeButton="false" />
      </template>

      <template #[`content-${pages.VALIDATION.pageNum}`]>
        <v-col class="pa-4">
          <Logo v-if="selectedProvider.logo" :logo="selectedProvider.logo" />
          <Spinner class="ma-auto mt-4" />
          <v-row class="ma-0 mt-2">
            <div class="mx-auto">Validating account, please wait...</div>
          </v-row>
        </v-col>
      </template>
      <!-- / Validation -->
    </ModalTunnel>
    <!-- / Modal Tunnel -->

    <!-- Top menu -->
    <v-col class="pa-0">
      <v-row class="ma-0">
        <v-col class="pa-0 token-text-h4"> Connected services </v-col>
      </v-row>
      <v-row class="ma-0 mt-8">
        <v-col class="pa-0">
          <v-row class="ma-0 token-text-h5"> Manage connected services </v-row>
          <v-row
            class="ma-0 token-text-regular token-text-color-secondary mt-1"
          >
            You can connect multiple accounts per provider.
          </v-row>
        </v-col>
        <v-col class="pa-0 ml-auto fit-content">
          <CustomButton
            text="Add account"
            icon="add"
            :level="$enums.ButtonLevels.Secondary"
            :disabled="!providers.length"
            @click="openModal()"
          />
        </v-col>
      </v-row>
      <v-row class="ma-0 mt-4">
        <v-col class="pa-0">
          <Divider />
        </v-col>
      </v-row>
      <!-- / Top menu -->

      <template v-if="loading">
        <v-row class="ma-0 mt-4 token-text-regular token-text-color-secondary">
          Loading connected accounts...
        </v-row>
      </template>
      <!-- Provider List -->
      <template v-else>
        <template v-for="provider of providers" :key="provider.id">
          <v-row class="ma-0 mt-10">
            <v-col class="pa-0 pb-2 d-flex">
              <Logo
                v-if="provider.logo"
                :logo="provider.logo"
                class="mr-3 ml-0"
              />
              <span v-else class="token-text-h4 provider-title">
                {{ provider.name }}
              </span>
              <CustomButton
                class="ml-auto px-2"
                :level="$enums.ButtonLevels.Secondary"
                icon="add"
                @click="openModal(provider)"
              />
            </v-col>
          </v-row>
          <v-row
            class="ma-0 mt-2 account-row"
            v-for="account of accountsByProvider[provider.id]"
            :key="account.id"
          >
            <v-col class="pa-3 bar">
              <v-row class="ma-0">
                <span class="token-text-medium">
                  {{ account.attributes.name }}
                </span>
                <div class="ml-auto d-flex">
                  <CustomButton
                    class="mr-2"
                    :level="$enums.ButtonLevels.SmallIcon"
                    icon="edit"
                    @click="openModal(provider, account.id)"
                  />
                  <CustomButton
                    :level="$enums.ButtonLevels.SmallIcon"
                    icon="trash"
                    @click="deleteAccount(account.id)"
                  />
                </div>
              </v-row>
            </v-col>
          </v-row>
          <v-row
            v-if="!accountsByProvider[provider.id].length"
            class="ma-0 mt-2"
          >
            <v-col class="pa-0 token-text-regular token-text-color-secondary">
              No account connected yet.
            </v-col>
          </v-row>
        </template>
      </template>
      <!-- / Provider List -->
    </v-col>
  </div>
</template>

<script lang="ts">
import { Vue, Options } from "vue-class-component";
import { ref } from "vue";

import MPHDefault from "@/components/Modals/Partials/Headers/MPHDefault.vue";
import MPFDefault from "@/components/Modals/Partials/Footers/MPFDefault.vue";
import ModalTunnel from "@/components/UIElements/ModalTunnel.vue";

import Spinner from "@/components/UIElements/Spinner.vue";
import CustomButton from "@/components/UIElements/CustomButton.vue";
import Divider from "@/components/UIElements/Divider.vue";
import Modal from "@/components/UIElements/Modal.vue";
import CustomTextInput from "@/components/UIElements/CustomTextInput.vue";
import Logo from "@/components/UIElements/Logo.vue";
import {
  APIInfrastructureProvider,
  APIInfrastructureProviderAccount,
} from "@/typesAPI";
import { ToastrDescriptor } from "@/types";
import API from "@/api/wrapper";

type RequirementField = "secret_key" | "access_key_id" | "project_id";

type CloudProvider = {
  id: string;
  name: string;
  logo?: string;
};

type AccountPayload = {
  name: string;
  secret_key: string;
  access_key_id: string;
  project_id: string;
};

type Page = {
  pageNum: number;
  header?: string;
  nextLabel?: string;
  previousLabel?: string;
};

@Options({
  components: {
    ModalTunnel,
    MPHDefault,
    MPFDefault,
    Spinner,
    CustomButton,
    Divider,
    Modal,
    CustomTextInput,
    Logo,
  },
})
export default class ConnectedServicesView extends Vue {
  providerRequirements: Record<string, RequirementField[]> = {
    hetzner: ["secret_key"],
    scaleway: ["project_id", "access_key_id", "secret_key"],
    outscale: ["access_key_id", "secret_key"],
  };

  loading = false;
  saveLoading = false;
  showModal = false;
  selectedProviderId = "hetzner";
  editingAccountId: string | null = null;
  form: AccountPayload = this.emptyForm();
  validatingConnection = false;

  refTunnel: any = ref(null);
  nbTunnelPages = 1;
  pageKeys = {
    PROVIDER: "PROVIDER",
    API_KEY: "API_KEY",
    VALIDATION: "VALIDATION",
  };
  pages: { [key: string]: Page } = {
    [this.pageKeys.PROVIDER]: {
      pageNum: 0,
      nextLabel: "Continue",
      previousLabel: "Cancel",
    },
    [this.pageKeys.API_KEY]: {
      pageNum: 1,
      nextLabel: "Validate connection",
      previousLabel: "Previous",
    },
    [this.pageKeys.VALIDATION]: {
      pageNum: 2,
    },
  };
  currentPageKey = "PROVIDER";

  goToPage(pageKey: string) {
    this.currentPageKey = pageKey;
    this.refTunnel?.goToPage(this.pages[pageKey].pageNum);
  }

  get disableNextButton(): boolean {
    if (this.currentPageKey === this.pageKeys.API_KEY) {
      return !this.editingAccountId && !this.isFormValid;
    }

    return false;
  }

  get selectedProvider(): CloudProvider | undefined {
    return this.providers.find(
      (provider) => provider.id === this.selectedProviderId,
    );
  }

  get header(): string {
    return this.editingAccountId
      ? `Edit ${this.selectedProvider?.name + " "}connection`
      : `Add new ${this.selectedProvider?.name + " "}connection`;
  }

  get accounts(): APIInfrastructureProviderAccount[] {
    return this.$store.getters["providerAccounts/getAccountList"];
  }

  get APIProviders(): APIInfrastructureProvider[] {
    return this.$store.getters["clusters/getProviders"];
  }

  get providers(): CloudProvider[] {
    return this.APIProviders.map((provider) => {
      const providerId = this.normalizeProviderId(provider.id);
      return {
        id: providerId,
        name: provider.attributes.name,
        logo: this.$enums.ProviderFullLogos[providerId],
      };
    });
  }

  get selectedProviderRequirements(): RequirementField[] {
    return this.providerRequirements[this.selectedProviderId] || ["secret_key"];
  }

  get accountsByProvider(): Record<string, APIInfrastructureProviderAccount[]> {
    const grouped: Record<string, APIInfrastructureProviderAccount[]> = {};
    this.providers.forEach((provider) => {
      grouped[provider.id] = [];
    });

    this.accounts.forEach((account) => {
      const providerId = this.normalizeProviderId(account.attributes.provider);
      if (!grouped[providerId]) {
        grouped[providerId] = [];
      }
      grouped[providerId].push(account);
    });

    return grouped;
  }

  get isFormValid(): boolean {
    if (!this.form.name.trim()) {
      return false;
    }
    return this.selectedProviderRequirements.every(
      (field) => !!this.form[field].trim(),
    );
  }

  emptyForm(): AccountPayload {
    return {
      name: "",
      secret_key: "",
      access_key_id: "",
      project_id: "",
    };
  }

  selectProvider(provider: CloudProvider) {
    this.selectedProviderId = provider.id;
    this.form.name = `${provider.name} connection`;
  }

  normalizeProviderId(providerId: string): string {
    return providerId.toLowerCase();
  }

  fieldLabel(field: RequirementField): string {
    return field
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }

  mapProvider(provider: APIInfrastructureProvider): CloudProvider {
    const providerId = this.normalizeProviderId(provider.id);
    return {
      id: providerId,
      name: provider.attributes.name,
      logo: this.$enums.ProviderFullLogos[providerId],
    };
  }

  buildPayload(): {
    name: string;
    secret_key: string;
    project_id?: string;
    access_key_id?: string;
  } {
    const payload: {
      name: string;
      secret_key: string;
      project_id?: string;
      access_key_id?: string;
    } = {
      name: this.form.name.trim(),
      secret_key: this.form.secret_key.trim(),
    };

    if (this.selectedProviderRequirements.includes("project_id")) {
      payload.project_id = this.form.project_id.trim();
    }
    if (this.selectedProviderRequirements.includes("access_key_id")) {
      payload.access_key_id = this.form.access_key_id.trim();
    }

    return payload;
  }

  openModal(provider?: CloudProvider, accountId?: string) {
    this.form = this.emptyForm();
    this.selectProvider(provider || this.providers[0]);
    if (provider) {
      this.goToPage(this.pageKeys.API_KEY);
    } else {
      this.goToPage(this.pageKeys.PROVIDER);
    }
    this.selectedProviderId = provider?.id || this.selectedProviderId;
    this.editingAccountId = accountId || null;

    if (accountId) {
      const account = this.accounts.find(
        (existingAccount) => existingAccount.id === accountId,
      );
      if (account) {
        this.form.name = account.attributes.name || "";
        this.form.project_id = account.attributes.project_id || "";
        this.form.access_key_id = account.attributes.access_key_id || "";
      }
    }

    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
    this.editingAccountId = null;
    this.form = this.emptyForm();
  }

  saveAccount() {
    this.saveLoading = true;
    const payload = this.buildPayload();

    if (this.editingAccountId) {
      Object.keys(payload).forEach((key: string) => {
        // If the field is empty or unchanged, remove it from the payload to avoid unnecessary updates
        if (
          !payload[key as keyof typeof payload] ||
          payload[key as keyof typeof payload] ===
            this.accounts.find((a) => a.id === this.editingAccountId)
              ?.attributes[
              key as keyof APIInfrastructureProviderAccount["attributes"]
            ]
        )
          delete payload[key as keyof typeof payload];
      });

      API.infrastructureProviderAccounts
        .edit(this.editingAccountId, payload)
        .then((res: APIInfrastructureProviderAccount) => {
          this.$store.commit("providerAccounts/EDIT_ACCOUNT", res);
          const descriptor: ToastrDescriptor = {
            title: "",
            text: `Account successfully updated.`,
            severity: this.$enums.Severity.SUCCESS,
            id: new Date().getTime().toString(),
          };
          this.$store.dispatch("toastrs/addDescriptor", descriptor);
          this.closeModal();
          this.$store.dispatch(
            "clusters/refreshLocations",
            this.selectedProviderId,
          );
        })
        .catch(() => {
          this.goToPage(this.pageKeys.API_KEY);
        })
        .finally(() => {
          this.saveLoading = false;
        });
    } else {
      if (!this.isFormValid) {
        return;
      }
      API.infrastructureProviderAccounts
        .create(this.selectedProviderId, payload)
        .then((res: APIInfrastructureProviderAccount) => {
          this.$store.commit("providerAccounts/CREATE_ACCOUNT", res);
          const descriptor: ToastrDescriptor = {
            title: "",
            text: `Account successfully connected.`,
            severity: this.$enums.Severity.SUCCESS,
            id: new Date().getTime().toString(),
          };
          this.$store.dispatch("toastrs/addDescriptor", descriptor);
          this.closeModal();
          this.$store.dispatch(
            "clusters/refreshLocations",
            this.selectedProviderId,
          );
        })
        .catch(() => {
          this.goToPage(this.pageKeys.API_KEY);
        })
        .finally(() => {
          this.saveLoading = false;
        });
    }
  }

  deleteAccount(accountId: string) {
    API.infrastructureProviderAccounts
      .delete(accountId)
      .then(() => {
        this.$store.commit("providerAccounts/DELETE_ACCOUNT", accountId);
        const descriptor: ToastrDescriptor = {
          title: "",
          text: `Account successfully deleted.`,
          severity: this.$enums.Severity.SUCCESS,
          id: new Date().getTime().toString(),
        };
        this.$store.dispatch("toastrs/addDescriptor", descriptor);

        const provider = this.accounts.find((a) => a.id === accountId)
          ?.attributes.provider;
        if (provider) {
          this.$store.dispatch(
            "clusters/refreshLocations",
            this.normalizeProviderId(provider),
          );
        }
      })
      .catch(() => {
        // Handle error if needed
      });
  }
}
</script>

<style lang="scss" scoped>
@import "@/css/variables";

.provider-grid {
  gap: 8px;
}

.provider-option {
  border: 1px solid $color-neutral-grey-12;
  border-radius: 8px;
  box-shadow: $shadow-light-btn;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 76px;
  width: calc(33% - 8px);
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &.selected {
    border-color: $color-brand;
    box-shadow: 0px 0px 0px 1px rgba(50, 25, 178, 0.24);
  }

  .provider-option-label {
    color: $color-neutral-black;
  }
}

.provider-logo {
  width: 75%;
}

.provider-title {
  text-transform: uppercase;
}

.field-label {
  text-transform: capitalize;
}

.account-row {
  width: 100%;
}
</style>
